import { deviceInfo } from './deviceInfo';
import Constants from 'expo-constants';

import {
  logger,
  configLoggerType,
  consoleTransport,
  fileAsyncTransport,
  defLvlType,
} from 'react-native-logs';
import { Colors, theme } from './theme';
import { FS } from '../libs/fileSystem';
import { Platform } from 'react-native';
import { setExceptionHandler } from '../libs/exeptionHandler';

// INIZIALIZZO VARIABILI D'AMBIENTE
// NEGOZIO TEST: 62480

const DEV = /* true; // */ __DEV__;

interface EnvVars {
  mode: 'test' | 'production';
  deviceInfo: {
    system: string;
    app: {
      version: string;
      name: string;
      editor: string;
    };
  };
  backend: {
    uri: string;
    config: string;
  };
  api: {
    uri: string;
    storage: string;
  };
  auth: {
    sessionId: string;
    uri: string;
    facebookId: string;
  };
  editor: {
    uri: string;
  };
  db: {
    cacheKey: string;
    authKey: string;
    appKey: string;
  };
  payment: {
    stripe: {
      publishableKey: string;
    };
  };
  storeLink: {
    ios: string;
    android: string;
  };
}

const ENV_VAR_TEST: EnvVars = {
  mode: 'test',
  deviceInfo,
  backend: {
    uri: 'https://test.backend.easypixy.com/graphql',
    config: 'https://config.easypixy.com/',
  },
  api: {
    uri: 'https://test.backend.easypixy.com/api',
    storage: 'https://test.storage.easypixy.com/storage',
  },
  auth: {
    sessionId: Constants.sessionId,
    uri: 'https://test.auth.easypixy.com',
    facebookId: '331064762419389',
  },
  editor: {
    uri: 'https://app.easypixy.com/editor',
  },
  db: {
    cacheKey: 'EP_CACHE_T',
    authKey: 'EP_AUTH_T',
    appKey: 'EP_APP_T',
  },
  payment: {
    stripe: {
      publishableKey:
        'pk_test_51MX69KHggAEQ9q1bvLxhfyyuu5xbtxW789pHmtDehVFtDk4ORLGFjJOr4KdyPkQZvalE211Z8YjZdWQlGYKZLFEI00JbJlUWiI',
    },
  },
  storeLink: {
    ios: 'itms-apps://itunes.apple.com/app/apple-store/1596568342?mt=8',
    android: 'https://play.google.com/store/apps/details?id=com.easypixy',
  },
};

const ENV_VAR_PRODUCTION: EnvVars = {
  mode: 'production',
  deviceInfo,
  backend: {
    uri: 'https://backend.easypixy.com/graphql',
    config: 'https://config.easypixy.com/',
  },
  api: {
    uri: 'https://backend.easypixy.com/api',
    storage: 'https://storage.easypixy.com/storage',
  },
  auth: {
    sessionId: Constants.sessionId,
    uri: 'https://auth.easypixy.com',
    facebookId: '331064762419389',
  },
  editor: {
    uri: 'https://app.easypixy.com/editor',
  },
  db: {
    cacheKey: 'EP_CACHE_P',
    authKey: 'EP_AUTH_P',
    appKey: 'EP_APP_P',
  },
  payment: {
    stripe: {
      publishableKey:
        'pk_live_51MX69KHggAEQ9q1boAnNlHsm3kV6ia5G69MWWStc3SwG6Dd4o8H4YW42wZeKHIIemhpuo2yX83E3doen5L6rc6GA00KTkKhJto',
    },
  },
  storeLink: {
    ios: 'itms-apps://itunes.apple.com/app/apple-store/1596568342?mt=8',
    android: 'https://play.google.com/store/apps/details?id=com.easypixy',
  },
};

let ENV: EnvVars = DEV ? ENV_VAR_TEST : ENV_VAR_PRODUCTION;

// INIZIALIZZO LOGGER

let today = new Date();

const loggerConfig = {
  transport: props => {
    consoleTransport(props);
    if (Platform.OS !== 'web') {
      fileAsyncTransport(props);
    }
  },
  severity: DEV ? 'debug' : 'error',
  levels: {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    silly: 4,
  },
  transportOptions: {
    FS: FS,
    filePath: FS.DocumentDirectoryPath || null,
    fileName: `logs_${today.getDate()}-${today.getMonth()}-${today.getFullYear()}`,
    colors: {
      info: 'blueBright',
      warn: 'yellowBright',
      error: 'redBright',
    },
  },
  async: true,
  dateFormat: 'time',
  printLevel: true,
  printDate: true,
  enabled: true,
  printFileLine: true,
};

const LOG = logger.createLogger<keyof (typeof loggerConfig)['levels']>(loggerConfig);

if (!DEV) {
  setExceptionHandler((platform, error) => {
    LOG.error('Unhandled ' + platform + ' error catched:', error);
  });
}

// LOG AVVIO APP

if (DEV) {
  LOG.debug('EasyPixy started in Development');
}

// @ts-ignore
const isHermes = () => !!global.HermesInternal;

LOG.warn(
  `NEW SESSION | v. ${ENV.deviceInfo.app.version} | ${ENV.deviceInfo.system} | ENV: ${
    ENV.mode
  } | Engine: ${isHermes ? 'Hermes' : 'JSC'}`
);

let BLURHASH: { [Color in Colors]: string } = {
  yellow: 'LJQc92Oa~pIA~U-:RoM{~VjFIVxt',
  pink: 'LERL67|?}s0eyDR*i_xZ}ZIoEf-p',
  green: 'LJQc92Oa~pIA~U-:RoM{~VjFIVxt',
  blue: 'LHMIKNX:t.=yi[IpRR%3ysR3%3Xo',
  violet: 'LVP6wHjbt:n~tTa_n#fRtokDRNjY',
  red: 'L9P5TV8wD41P=xvLk=4n*K{I00#8',
  grey: 'LVP6wHjbt:n~tTa_n#fRtokDRNjY',
  dark: 'LVP6wHjbt:n~tTa_n#fRtokDRNjY',
  light: 'LVP6wHjbt:n~tTa_n#fRtokDRNjY',
};

const PLACEHOLDER =
  'data:image/jpeg;base64,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';

export { ENV, LOG, theme, PLACEHOLDER, BLURHASH };
